import { Minus, Plus, Redo2, Repeat, Undo2 } from 'lucide-react'
import { Button } from '@/components/ui/shadcn/button'
import { useCanvasStore } from './canvas-store'
import { computeFitTransform, zoomAroundPoint, type Rect } from './geometry'
import { animateViewTo } from './animate-view'

// Multiplicative step per zoom-button click. zoomAroundPoint clamps to [0.1, 4].
const ZOOM_STEP = 1.25

// Bottom-left canvas controls (mirrors the workspace canvas bar): zoom in / out, a
// click-to-reset zoom percentage, fit-everything-in-view, and undo/redo. Zoom is anchored
// on the viewport center.
type Props = {
  contentRects: Rect[]
  canUndo: boolean
  canRedo: boolean
  onUndo: () => void
  onRedo: () => void
}

export function CanvasControls({ contentRects, canUndo, canRedo, onUndo, onRedo }: Props) {
  const scale = useCanvasStore((s) => s.scale)
  const offset = useCanvasStore((s) => s.offset)
  const viewport = useCanvasStore((s) => s.viewport)
  const setTransform = useCanvasStore((s) => s.setTransform)

  const applyZoom = (nextScale: number) => {
    const center = { x: viewport.width / 2, y: viewport.height / 2 }
    const t = zoomAroundPoint({ scale, offset }, center, nextScale)
    setTransform({ scale: t.scale, offset: t.offset })
  }

  const fitToContent = () => {
    const t = computeFitTransform(contentRects, viewport)
    if (t) animateViewTo(t)
  }

  return (
    <div className="bg-card/95 border-border/60 absolute bottom-6 left-6 z-10 flex items-center gap-1 rounded-full border p-1 shadow-lg backdrop-blur">
      <Button
        variant="ghost"
        size="icon-sm"
        className="rounded-full"
        onClick={() => applyZoom(scale * ZOOM_STEP)}
        aria-label="Zoom in"
        title="Zoom in">
        <Plus />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="min-w-[3.25rem] justify-center rounded-full tabular-nums"
        onClick={() => applyZoom(1)}
        aria-label="Reset zoom to 100%"
        title="Reset to 100%">
        {Math.round(scale * 100)}%
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        className="rounded-full"
        onClick={() => applyZoom(scale / ZOOM_STEP)}
        aria-label="Zoom out"
        title="Zoom out">
        <Minus />
      </Button>

      <div className="bg-border/70 mx-0.5 h-6 w-px" aria-hidden />

      <Button
        variant="ghost"
        size="icon-sm"
        className="rounded-full"
        onClick={fitToContent}
        disabled={contentRects.length === 0}
        aria-label="Fit everything in view"
        title="Fit everything in view">
        <Repeat />
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        className="rounded-full"
        onClick={onUndo}
        disabled={!canUndo}
        aria-label="Undo"
        title="Undo">
        <Undo2 />
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        className="rounded-full"
        onClick={onRedo}
        disabled={!canRedo}
        aria-label="Redo"
        title="Redo">
        <Redo2 />
      </Button>
    </div>
  )
}
